<template>
    <div class="chat-page">
        <ChatWindow
            :conversations="conversations"
            :active-conversation="activeConversation"
            :messages="messages"
            :current-user-id="currentUserId"
            :user-type="userType"
            :online-users="onlineUsers"
            :typing-users="typingUsers"
            :total-unread="totalUnread"
            :loading-conversations="loadingConversations"
            :loading-messages="loadingMessages"
            :has-more-messages="hasMoreMessages"
            :show-close-button="false"
            :max-file-size="maxFileSize"
            :allowed-file-types="allowedFileTypes"
            @select-conversation="selectConversation"
            @clear-active="clearActiveConversation"
            @send-message="handleSendMessage"
            @typing-start="startTyping"
            @typing-stop="stopTyping"
            @load-more-messages="loadMoreMessages"
            @file-select="handleFileUpload"
            @new-conversation="showSellerSelectModal = true"
            @show-info="showConversationInfo"
            @menu-action="handleMenuAction"
        />

        <!-- Seller Selection Modal (for Account Managers) -->
        <div v-if="showSellerSelectModal" class="modal-overlay" @click.self="showSellerSelectModal = false">
            <div class="modal-content">
                <div class="modal-header">
                    <h3>Nouvelle conversation</h3>
                    <button class="close-btn" @click="showSellerSelectModal = false">
                        <i class="material-icons">close</i>
                    </button>
                </div>
                <div class="modal-body">
                    <div class="search-box">
                        <i class="material-icons">search</i>
                        <input
                            type="text"
                            v-model="sellerSearch"
                            placeholder="Rechercher un vendeur..."
                            @input="searchSellers"
                        />
                    </div>

                    <div v-if="loadingSellers" class="loading">
                        <div class="spinner"></div>
                    </div>

                    <div v-else-if="availableSellers.length === 0" class="empty-state">
                        <p>Aucun vendeur trouvé</p>
                    </div>

                    <div v-else class="sellers-list">
                        <div
                            v-for="seller in availableSellers"
                            :key="seller._id"
                            class="seller-option"
                            @click="startNewConversation(seller)"
                        >
                            <div class="seller-avatar">
                                <span>{{ getInitial(seller) }}</span>
                            </div>
                            <div class="seller-info">
                                <span class="name">{{ seller.fullName || seller.email }}</span>
                                <span class="country">{{ seller.country }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import ChatWindow from '@/components/Chat/ChatWindow.vue';
import { socketService } from '@/plugins/socket';
import debounce from 'lodash/debounce';

export default {
    name: 'ChatPage',
    components: {
        ChatWindow
    },
    data() {
        return {
            showSellerSelectModal: false,
            availableSellers: [],
            sellerSearch: '',
            loadingSellers: false
        };
    },
    computed: {
        ...mapState('chat', [
            'conversations',
            'activeConversation',
            'messages',
            'typingUsers',
            'onlineUsers',
            'loading',
            'loadingMessages'
        ]),
        loadingConversations() {
            return this.loading;
        },
        hasMoreMessages() {
            if (!this.activeConversation) return false;
            const hasMore = this.$store.state.chat.hasMoreMessages;
            return hasMore && hasMore[this.activeConversation._id] || false;
        },
        ...mapGetters('chat', ['totalUnread']),
        currentUserId() {
            const user = this.$store.state.user;
            return (user && user._id) || '';
        },
        userType() {
            const user = this.$store.state.user;
            return (user && user.type) || 'Seller';
        },
        maxFileSize() {
            const settings = this.$store.state.settingsGlobal;
            return (settings && settings.chat && settings.chat.maxFileSize) || 10485760;
        },
        allowedFileTypes() {
            const settings = this.$store.state.settingsGlobal;
            return (settings && settings.chat && settings.chat.allowedFileTypes) ||
                   ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'xls', 'xlsx'];
        },
        conversationIdFromRoute() {
            return this.$route.params.conversationId;
        }
    },
    watch: {
        conversationIdFromRoute(conversationId, oldId) {
            // Only handle route changes after initial load
            if (conversationId && conversationId !== oldId) {
                this.openConversationFromRoute(conversationId);
            }
        }
    },
    created() {
        this.searchSellers = debounce(this.doSearchSellers, 300);
    },
    async mounted() {
        await this.initializeChat();
    },
    beforeDestroy() {
        // Leave active conversation
        if (this.activeConversation) {
            socketService.leaveConversation(this.activeConversation._id);
        }
    },
    methods: {
        ...mapActions('chat', [
            'initSocket',
            'setActiveConversation'
        ]),
        async initializeChat() {
            console.log('[ChatPage] initializeChat started');
            try {
                // Initialize socket connection
                try {
                    this.initSocket();
                    console.log('[ChatPage] Socket initialized');
                } catch (e) {
                    console.error('[ChatPage] Socket init error:', e);
                }

                // Fetch conversations via API
                console.log('[ChatPage] Loading conversations...');
                await this.loadConversations();
                console.log('[ChatPage] Conversations loaded');

                // If conversation ID in route, open it
                if (this.conversationIdFromRoute) {
                    console.log('[ChatPage] Opening conversation from route:', this.conversationIdFromRoute);
                    await this.openConversationFromRoute(this.conversationIdFromRoute);
                }
            } catch (error) {
                console.error('[ChatPage] Failed to initialize chat:', error);
            }
        },
        async loadConversations() {
            console.log('[ChatPage] loadConversations - setting loading true');
            this.$store.commit('chat/SET_LOADING', true);
            try {
                const response = await this.$server.getMyConversations('conversations', { limit: 100 });
                console.log('[ChatPage] Conversations API response:', response);
                if (response && response.content) {
                    // Handle both array and paginated results format
                    const results = response.content.results || response.content;
                    console.log('[ChatPage] Parsed results:', results);
                    if (Array.isArray(results)) {
                        console.log('[ChatPage] Setting', results.length, 'conversations');
                        this.$store.commit('chat/SET_CONVERSATIONS', results);
                        // Set unread counts
                        results.forEach(conv => {
                            const unread = conv.unreadBySeller || conv.unreadByAM || 0;
                            this.$store.commit('chat/SET_UNREAD_COUNT', { conversationId: conv._id, count: unread });
                        });
                    } else {
                        console.log('[ChatPage] Results not an array, setting empty');
                        this.$store.commit('chat/SET_CONVERSATIONS', []);
                    }
                } else {
                    console.log('[ChatPage] No content in response, setting empty');
                    this.$store.commit('chat/SET_CONVERSATIONS', []);
                }
            } catch (error) {
                console.error('[ChatPage] Failed to load conversations:', error);
                this.$store.commit('chat/SET_CONVERSATIONS', []);
            } finally {
                console.log('[ChatPage] Setting loading false');
                this.$store.commit('chat/SET_LOADING', false);
            }
        },
        async openConversationFromRoute(conversationId) {
            // Find conversation in list
            let conversation = this.conversations.find(c => c._id === conversationId);

            if (!conversation) {
                // Fetch conversation if not in list
                try {
                    const response = await this.$server.getConversation('conversations', { id: conversationId });
                    if (response && response.content) {
                        conversation = response.content;
                        this.$store.commit('chat/ADD_CONVERSATION', conversation);
                    }
                } catch (error) {
                    console.error('Failed to fetch conversation:', error);
                    return;
                }
            }

            if (conversation) {
                await this.selectConversation(conversation);
            }
        },
        async selectConversation(conversation) {
            // Leave previous conversation room
            if (this.activeConversation && this.activeConversation._id !== conversation._id) {
                socketService.leaveConversation(this.activeConversation._id);
            }

            // Set active and join room
            this.setActiveConversation(conversation);
            socketService.joinConversation(conversation._id);

            // Mark as read
            socketService.markAsRead(conversation._id);
            this.$store.commit('chat/CLEAR_UNREAD', conversation._id);

            // Always load fresh messages when selecting a conversation
            await this.loadMessages(conversation._id);

            // Update URL
            if (this.$route.params.conversationId !== conversation._id) {
                this.$router.replace(`/account-manager/chat/${conversation._id}`);
            }
        },
        async loadMessages(conversationId, page = 1) {
            this.$store.commit('chat/SET_LOADING_MESSAGES', true);
            try {
                const response = await this.$server.getMessages('messages', {
                    conversationId,
                    page,
                    limit: 50
                });
                if (response && response.content && response.content.results) {
                    const append = page > 1;
                    this.$store.commit('chat/SET_MESSAGES', {
                        conversationId,
                        messages: response.content.results,
                        append
                    });
                    const hasMore = response.content.results.length >= 50;
                    this.$store.commit('chat/SET_HAS_MORE_MESSAGES', { conversationId, hasMore });
                }
            } catch (error) {
                console.error('Failed to load messages:', error);
            } finally {
                this.$store.commit('chat/SET_LOADING_MESSAGES', false);
            }
        },
        clearActiveConversation() {
            if (this.activeConversation) {
                socketService.leaveConversation(this.activeConversation._id);
            }
            this.$store.commit('chat/SET_ACTIVE_CONVERSATION', null);

            // Update URL
            this.$router.replace('/account-manager/chat');
        },
        async handleSendMessage(data) {
            const { conversationId, content, type, attachment } = data;
            const tempId = `temp_${Date.now()}`;

            // Add optimistic message to store
            const tempMessage = {
                _id: tempId,
                tempId,
                conversation: conversationId,
                content,
                type: type || 'text',
                attachment,
                sender: { _id: this.currentUserId },
                status: 'sending',
                createdAt: new Date().toISOString()
            };
            this.$store.commit('chat/ADD_MESSAGE', { conversationId, message: tempMessage });

            // Try to send via socket, fall back to REST API if socket not connected
            const sent = socketService.sendMessage(conversationId, content, type || 'text', attachment, tempId);
            if (!sent) {
                console.log('[ChatPage] Socket send failed, using REST API fallback');
                try {
                    const response = await this.$server.sendMessage('messages', {
                        conversationId,
                        content,
                        type: type || 'text',
                        attachment
                    });
                    if (response && response.content) {
                        const msg = response.content;
                        // Update the temp message with the real one
                        this.$store.commit('chat/ADD_MESSAGE', {
                            conversationId,
                            message: { ...msg, tempId }
                        });
                        // Also update conversation's lastMessage
                        this.$store.commit('chat/UPDATE_CONVERSATION', {
                            conversationId,
                            updates: {
                                lastMessage: {
                                    content: msg.content,
                                    sender: msg.sender,
                                    sentAt: msg.createdAt,
                                    type: msg.type
                                },
                                updatedAt: msg.createdAt
                            }
                        });
                    }
                } catch (error) {
                    console.error('[ChatPage] REST API send failed:', error);
                    // Mark message as failed
                    tempMessage.status = 'failed';
                    this.$store.commit('chat/ADD_MESSAGE', { conversationId, message: tempMessage });
                }
            }
        },
        startTyping(conversationId) {
            socketService.startTyping(conversationId);
        },
        stopTyping(conversationId) {
            socketService.stopTyping(conversationId);
        },
        loadMoreMessages() {
            if (this.activeConversation && !this.loadingMessages && this.hasMoreMessages) {
                const currentMessages = this.messages[this.activeConversation._id] || [];
                const page = Math.ceil(currentMessages.length / 50) + 1;
                this.loadMessages(this.activeConversation._id, page);
            }
        },
        async handleFileUpload({ conversationId, file }) {
            try {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('conversationId', conversationId);

                const response = await this.$server.uploadFile('messages', formData);

                if (response && response.content) {
                    const type = file.type.startsWith('image/') ? 'image' : 'file';
                    this.handleSendMessage({
                        conversationId,
                        content: file.name,
                        type,
                        attachment: response.content
                    });
                }
            } catch (error) {
                console.error('File upload failed:', error);
                if (this.$toast) this.$toast.error('Échec du téléchargement du fichier');
            }
        },
        showConversationInfo(conversation) {
            // Navigate to seller stats
            const sellerId = conversation.seller && conversation.seller._id;
            if (sellerId) {
                this.$router.push(`/account-manager/sellers/${sellerId}`);
            }
        },
        handleMenuAction({ action, conversation }) {
            console.log('Menu action:', action, conversation);
            // Handle archive, mute, block actions
        },
        getInitial(seller) {
            if (!seller) return '';
            const name = seller.fullName || seller.firstName || seller.email || '';
            return name.charAt(0).toUpperCase();
        },
        async doSearchSellers() {
            if (!this.sellerSearch.trim()) {
                this.loadAvailableSellers();
                return;
            }

            this.loadingSellers = true;
            try {
                const response = await this.$server.getAssignedSellers('accountManager', {
                    search: this.sellerSearch,
                    limit: 20
                });

                if (response && response.content) {
                    this.availableSellers = response.content.results || [];
                }
            } catch (error) {
                console.error('Failed to search sellers:', error);
            } finally {
                this.loadingSellers = false;
            }
        },
        async loadAvailableSellers() {
            this.loadingSellers = true;
            try {
                const response = await this.$server.getAssignedSellers('accountManager', {
                    limit: 20
                });

                if (response && response.content) {
                    this.availableSellers = response.content.results || [];
                }
            } catch (error) {
                console.error('Failed to load sellers:', error);
            } finally {
                this.loadingSellers = false;
            }
        },
        async startNewConversation(seller) {
            try {
                const response = await this.$server.getOrCreateConversation('conversations', {
                    sellerId: seller._id
                });

                if (response && response.content) {
                    this.showSellerSelectModal = false;
                    // Add the new conversation to the store
                    this.$store.commit('chat/ADD_CONVERSATION', response.content);
                    await this.selectConversation(response.content);
                }
            } catch (error) {
                console.error('Failed to create conversation:', error);
                if (this.$toast) this.$toast.error('Erreur lors de la création de la conversation');
            }
        }
    }
};
</script>

<style scoped>
.chat-page {
    height: calc(100vh - 64px);
    background-color: #f3f4f6;
    padding: 24px;
}

.chat-page > :first-child {
    height: 100%;
}

/* Modal styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
}

.modal-content {
    background: #ffffff;
    border-radius: 16px;
    width: 90%;
    max-width: 480px;
    max-height: 80vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #111827;
}

.close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    background: none;
    border-radius: 50%;
    cursor: pointer;
    color: #6b7280;
    transition: all 0.2s;
}

.close-btn:hover {
    background-color: #f3f4f6;
    color: #111827;
}

.modal-body {
    padding: 20px 24px;
    overflow-y: auto;
    flex: 1;
}

.search-box {
    position: relative;
    margin-bottom: 16px;
}

.search-box i {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    font-size: 20px;
}

.search-box input {
    width: 100%;
    padding: 12px 12px 12px 40px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s;
}

.search-box input:focus {
    border-color: #059669;
}

.loading {
    display: flex;
    justify-content: center;
    padding: 32px;
}

.spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top-color: #059669;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.empty-state {
    text-align: center;
    padding: 32px;
    color: #9ca3af;
}

.sellers-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.seller-option {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.2s;
}

.seller-option:hover {
    background-color: #f3f4f6;
}

.seller-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 16px;
    flex-shrink: 0;
}

.seller-info {
    display: flex;
    flex-direction: column;
}

.seller-info .name {
    font-weight: 500;
    color: #111827;
    font-size: 14px;
}

.seller-info .country {
    font-size: 12px;
    color: #6b7280;
}

@media (max-width: 768px) {
    .chat-page {
        padding: 0;
        height: calc(100vh - 56px);
    }
}
</style>
